import { ConfigProvider, Form, Input, InputNumber, Select, Switch } from "antd";
import { MapPin } from "lucide-react";
import PropTypes from "prop-types";
import { BsShieldFillCheck, BsShieldFillExclamation } from "react-icons/bs";
import { getRateEditRows } from "../addCar/RateFields";
import CarDetailEditFieldTable from "./CarDetailEditFieldTable";
import {
    FORM_INPUT_CLASS,
    FORM_ITEM_CLASS,
    FORM_SELECT_STYLE,
    SELECT_CONFIG,
} from "./carDetailClasses";

function CarDetailEditForm({
    form,
    car,
    carCategories,
    isCarPricingMode,
    allLocationOptions,
    selectedLocationId,
    onChangeSelectedLocation,
    selectedColor,
    colorOptions,
    onChangeSelectedColor,
    carRate,
    setCarRate,
    onFinish,
    t,
}) {
    const locationValue = selectedLocationId
        ? (() => {
              const selOpt = allLocationOptions.find((loc) => loc.value === selectedLocationId);
              const displayText = selOpt?.searchLabel || car?.baseLocation || selectedLocationId;
              return {
                  value: selectedLocationId,
                  label: (
                      <div className="flex items-center gap-2">
                          {selOpt?.isInsuranceEnabled ? (
                              <BsShieldFillCheck className="text-sm text-green-500" title="Insurance enabled" />
                          ) : (
                              <BsShieldFillExclamation className="text-sm text-amber-500" title="Insurance not enabled" />
                          )}
                          <span>{displayText}</span>
                      </div>
                  ),
              };
          })()
        : undefined;

    const rows = [
        {
            label: t("plateNumber"),
            content: (
                <Form.Item
                    name="carNumber"
                    className={FORM_ITEM_CLASS}
                    rules={[{ required: true, message: t("carDetail.plateNumberRequired") }]}>
                    <Input type="text" className={FORM_INPUT_CLASS} placeholder={t("carDetail.enterPlateNumber")} />
                </Form.Item>
            ),
        },
        {
            label: t("category"),
            content: (
                <ConfigProvider theme={SELECT_CONFIG}>
                    <Form.Item name="category" className={FORM_ITEM_CLASS}>
                        <Select
                            showSearch
                            optionFilterProp="label"
                            style={FORM_SELECT_STYLE}
                            onChange={(value) => {
                                const category = carCategories.find((cat) => String(cat._id) === value);

                                if (isCarPricingMode) {
                                    form.setFieldsValue({ category: value });
                                    return;
                                }

                                form.setFieldsValue({
                                    category: value,
                                    config: {
                                        ...form.getFieldValue("config"),
                                        seats: category?.seats,
                                        bags: category?.bags,
                                    },
                                });
                            }}
                            options={carCategories.map((category) => ({
                                value: String(category._id),
                                label: category.categoryName,
                            }))}
                        />
                    </Form.Item>
                </ConfigProvider>
            ),
        },
        {
            label: t("location"),
            content: (
                <ConfigProvider theme={SELECT_CONFIG}>
                    <Select
                        suffixIcon={<MapPin size={12} />}
                        labelInValue
                        showSearch
                        optionFilterProp="label"
                        style={FORM_SELECT_STYLE}
                        onChange={onChangeSelectedLocation}
                        value={locationValue}
                        options={allLocationOptions}
                        filterOption={(input, option) =>
                            (option?.searchLabel ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </ConfigProvider>
            ),
        },
        {
            label: t("carDetail.movetraxKey"),
            content: (
                <Form.Item
                    name="serialNumber"
                    className={FORM_ITEM_CLASS}
                    rules={[{ required: true, message: t("carDetail.movetraxKeyRequired") }]}>
                    <Input type="text" className={FORM_INPUT_CLASS} />
                </Form.Item>
            ),
        },
        {
            label: t("vinNumberShort"),
            content: (
                <Form.Item
                    name="VIN"
                    className={FORM_ITEM_CLASS}
                    rules={[{ required: true, message: t("carDetail.vinRequired") }]}>
                    <Input type="text" className={FORM_INPUT_CLASS} placeholder={t("carDetail.enterVIN")} />
                </Form.Item>
            ),
        },
        {
            label: t("carDetail.make"),
            content: (
                <Form.Item
                    name={["config", "make"]}
                    className={FORM_ITEM_CLASS}
                    rules={[{ required: true, message: t("carDetail.makeRequired") }]}>
                    <Input type="text" className={FORM_INPUT_CLASS} placeholder={t("carDetail.enterMake")} />
                </Form.Item>
            ),
        },
        {
            label: t("model"),
            content: (
                <Form.Item
                    name={["config", "model"]}
                    className={FORM_ITEM_CLASS}
                    rules={[{ required: true, message: t("carDetail.modelRequired") }]}>
                    <Input type="text" className={FORM_INPUT_CLASS} placeholder={t("carDetail.enterModel")} />
                </Form.Item>
            ),
        },
        {
            label: t("year"),
            content: (
                <Form.Item
                    name={["config", "year"]}
                    className={FORM_ITEM_CLASS}
                    rules={[{ required: true, message: t("carDetail.yearRequired") }]}>
                    <Input type="number" className={FORM_INPUT_CLASS} placeholder={t("carDetail.enterYear")} />
                </Form.Item>
            ),
        },
        {
            label: t("color"),
            content: (
                <ConfigProvider theme={SELECT_CONFIG}>
                    <Select
                        labelInValue
                        showSearch
                        optionFilterProp="children"
                        style={FORM_SELECT_STYLE}
                        onChange={onChangeSelectedColor}
                        value={selectedColor || undefined}
                        options={colorOptions}
                        filterOption={(input, option) =>
                            (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                    />
                </ConfigProvider>
            ),
        },
        {
            label: t("bags"),
            content: (
                <Form.Item
                    name={["config", "bags"]}
                    className={FORM_ITEM_CLASS}
                    rules={[{ required: true, message: t("carDetail.doorsRequired") }]}>
                    <Input
                        disabled={!isCarPricingMode}
                        type="number"
                        className={FORM_INPUT_CLASS}
                        min={isCarPricingMode ? 0 : 1}
                    />
                </Form.Item>
            ),
        },
        {
            label: t("seats"),
            content: (
                <Form.Item
                    name={["config", "seats"]}
                    className={FORM_ITEM_CLASS}
                    rules={[{ required: true, message: t("carDetail.seatsRequired") }]}>
                    <Input disabled={!isCarPricingMode} type="number" className={FORM_INPUT_CLASS} min={1} />
                </Form.Item>
            ),
        },
        ...(isCarPricingMode ? getRateEditRows(carRate, setCarRate, t) : []),
        {
            label: t("fuelType"),
            content: (
                <Form.Item name={["config", "fuelType"]} className={FORM_ITEM_CLASS}>
                    <Input type="text" className={FORM_INPUT_CLASS} placeholder={t("carDetail.enterFuelType")} />
                </Form.Item>
            ),
        },
        {
            label: t("carDetail.transmission"),
            content: (
                <Form.Item name={["config", "transmission"]} className={FORM_ITEM_CLASS}>
                    <Input type="text" className={FORM_INPUT_CLASS} placeholder={t("carDetail.enterTransmission")} />
                </Form.Item>
            ),
        },
        {
            label: t("ezPassTagNumber"),
            content: (
                <Form.Item name="ezPassTagNumber" className={FORM_ITEM_CLASS}>
                    <Input type="text" className={FORM_INPUT_CLASS} placeholder={t("ezPassTagNumber")} />
                </Form.Item>
            ),
        },
        {
            label: t("worthValue"),
            content: (
                <Form.Item
                    name="worthValue"
                    className={FORM_ITEM_CLASS}
                    getValueFromEvent={(value) => {
                        if (value === "" || value === null || value === undefined) {
                            return undefined;
                        }
                        return typeof value === "string" ? parseFloat(value) || undefined : value;
                    }}>
                    <InputNumber
                        min={0}
                        prefix="$"
                        className={`${FORM_INPUT_CLASS} !w-full`}
                        placeholder={t("worthValue")}
                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                    />
                </Form.Item>
            ),
        },
        {
            label: t("carDetail.isFeatured"),
            content: (
                <Form.Item name="isFeatured" valuePropName="checked" className={FORM_ITEM_CLASS}>
                    <Switch />
                </Form.Item>
            ),
        },
    ];

    return (
        <Form form={form} initialValues={car} onFinish={onFinish} layout="vertical" className="pt-2">
            <CarDetailEditFieldTable title={t("carDetail.carDetails")} rows={rows} />
        </Form>
    );
}

CarDetailEditForm.propTypes = {
    form: PropTypes.shape({
        setFieldsValue: PropTypes.func.isRequired,
        getFieldValue: PropTypes.func.isRequired,
    }).isRequired,
    car: PropTypes.object.isRequired,
    carCategories: PropTypes.arrayOf(PropTypes.object).isRequired,
    isCarPricingMode: PropTypes.bool.isRequired,
    allLocationOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
    selectedLocationId: PropTypes.string,
    onChangeSelectedLocation: PropTypes.func.isRequired,
    selectedColor: PropTypes.string,
    colorOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
    onChangeSelectedColor: PropTypes.func.isRequired,
    carRate: PropTypes.object.isRequired,
    setCarRate: PropTypes.func.isRequired,
    onFinish: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};

export default CarDetailEditForm;
